import { useState, useEffect } from "react";
import { Users, ChevronLeft, Plus, Globe } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useQuery } from "../../lib/useQuery";
import { supabase } from "../../lib/supabase";
import { useToast } from "../Toast";
import LoadingSkeleton from "../LoadingSkeleton";
import LeadsList from "./LeadsList";
import LeadForm from "./LeadForm";
import LeadCaptureDrawer from "./LeadCaptureDrawer";

const DEFAULT_OPTIONS = {
  conocio: [
    { name: "Barrio", sort_order: 1 },
    { name: "Otro cliente", sort_order: 2 },
    { name: "Redes Sociales", sort_order: 3 },
    { name: "Anuncio o campaña", sort_order: 4 },
    { name: "Panfleto", sort_order: 5 },
  ],
  canal: [
    { name: "WhatsApp", sort_order: 1 },
    { name: "Instagram", sort_order: 2 },
    { name: "Puerta", sort_order: 3 },
    { name: "Web", sort_order: 4 },
    { name: "Teléfono", sort_order: 5 },
    { name: "Otro", sort_order: 6 },
  ],
  servicio: [
    { name: "Grupal", sort_order: 1 },
    { name: "Personal", sort_order: 2 },
    { name: "Pilates", sort_order: 3 },
    { name: "Otro", sort_order: 4 },
  ],
};

const OPTION_CONFIG = {
  conocio: { table: "conocio_options" },
  canal: { table: "canal_options" },
  servicio: { table: "servicio_options" },
};

export default function ControlLeads({ onBack }) {
  const { user } = useAuth();
  const { showError, showSuccess } = useToast();
  const [showForm, setShowForm] = useState(false);
  const [showBuzon, setShowBuzon] = useState(false);
  const [editingLead, setEditingLead] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savingOptions, setSavingOptions] = useState(false);
  const [filters, setFilters] = useState({
    mes: String(new Date().getMonth() + 1),
    mesBy: "contacto",
    estado: "",
    canal: "",
    servicio: "",
  });

  const { data: leads, loading, refetch } = useQuery(async () => {
    const { data, error } = await supabase
      .from("leads")
      .select("*")
      .eq("student_id", user.id)
      .order("fecha_contacto", { ascending: false });
    if (error) throw error;
    return data || [];
  }, [user.id]);

  const { data: conocioOptions, loading: l1, refetch: r1 } = useQuery(async () => {
    const { data, error } = await supabase.from("conocio_options").select("*").eq("student_id", user.id).order("sort_order");
    if (error) throw error;
    return data || [];
  }, [user.id]);

  const { data: canalOptions, loading: l2, refetch: r2 } = useQuery(async () => {
    const { data, error } = await supabase.from("canal_options").select("*").eq("student_id", user.id).order("sort_order");
    if (error) throw error;
    return data || [];
  }, [user.id]);

  const { data: servicioOptions, loading: l3, refetch: r3 } = useQuery(async () => {
    const { data, error } = await supabase.from("servicio_options").select("*").eq("student_id", user.id).order("sort_order");
    if (error) throw error;
    return data || [];
  }, [user.id]);

  const allOptions = { conocio: conocioOptions, canal: canalOptions, servicio: servicioOptions };
  const refetchers = { conocio: r1, canal: r2, servicio: r3 };

  useEffect(() => {
    const init = async () => {
      for (const type of ["conocio", "canal", "servicio"]) {
        const opts = allOptions[type];
        if (opts && opts.length === 0) {
          const inserts = DEFAULT_OPTIONS[type].map(o => ({ student_id: user.id, name: o.name, sort_order: o.sort_order }));
          await supabase.from(OPTION_CONFIG[type].table).insert(inserts);
          refetchers[type]();
        }
      }
    };
    if (!l1 && !l2 && !l3) init();
  }, [l1, l2, l3]);

  const handleSaveOptions = async (type, items) => {
    setSavingOptions(true);
    const table = OPTION_CONFIG[type].table;
    const currentOptions = allOptions[type] || [];

    try {
      const existingIds = currentOptions.map(o => o.id);
      const newItemIds = items.filter(i => !i.isNew).map(i => i.id);
      const toDelete = existingIds.filter(id => !newItemIds.includes(id));

      if (toDelete.length > 0) {
        await supabase.from(table).delete().in("id", toDelete);
      }

      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.isNew) {
          await supabase.from(table).insert({ student_id: user.id, name: item.name, sort_order: i + 1 });
        } else {
          await supabase.from(table).update({ name: item.name, sort_order: i + 1 }).eq("id", item.id);
        }
      }

      await refetchers[type]();
      showSuccess("Opciones guardadas");
    } catch (err) {
      showError("No se pudieron guardar las opciones");
      throw err;
    } finally {
      setSavingOptions(false);
    }
  };

  const handleNew = () => {
    setEditingLead(null);
    setShowForm(true);
  };

  const handleEdit = (lead) => {
    setEditingLead(lead);
    setShowForm(true);
  };

  const handleClose = () => {
    setShowForm(false);
    setEditingLead(null);
  };

  const handleSave = async (formData) => {
    setSaving(true);
    try {
      const llamadasLimpias = (formData.llamadas || []).map(l => ({
        fecha: l.fecha || "",
        resultado: l.resultado || "",
        realizada: !!l.realizada,
      }));
      const payload = {
        student_id: user.id,
        estado: formData.estado,
        nombre: formData.nombre,
        fecha_contacto: formData.fecha_contacto,
        resultado: formData.resultado || null,
        fecha_resultado: formData.fecha_resultado || null,
        canal: formData.canal || null,
        servicio: formData.servicio || null,
        sesion_prueba: formData.sesion_prueba,
        telefono: formData.telefono || null,
        email: formData.email || null,
        como_conocio: formData.como_conocio || null,
        observaciones: formData.observaciones || null,
        llamadas: llamadasLimpias,
        seguimiento: formData.seguimiento || null,
        updated_at: new Date().toISOString(),
      };

      if (editingLead?.id) {
        const { error } = await supabase.from("leads").update(payload).eq("id", editingLead.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("leads").insert(payload);
        if (error) throw error;
      }

      await refetch();
      showSuccess(editingLead?.id ? "Lead actualizado" : "Lead creado correctamente");
      handleClose();
    } catch (err) {
      showError("No se pudo guardar el lead. Inténtalo de nuevo.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    const { error } = await supabase.from("leads").delete().eq("id", id);
    if (error) {
      showError("No se pudo eliminar el lead");
      return;
    }
    refetch();
  };

  if (loading || l1 || l2 || l3) return <LoadingSkeleton />;

  return (
    <div className="animate-fade-in">
      <button onClick={onBack} className="flex items-center gap-1 text-gray-500 hover:text-gray-700 text-sm mb-6">
        <ChevronLeft size={18} /> Volver a Herramientas
      </button>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center">
            <Users size={24} className="text-emerald-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">Control de Leads</h2>
            <p className="text-sm text-gray-500">{leads?.length || 0} lead(s) registrado(s)</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowBuzon(true)}
            className="px-4 py-2.5 bg-white border border-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
          >
            <Globe size={18} />
            Buzón web
          </button>
          <button
            onClick={handleNew}
            className="px-4 py-2.5 bg-brand text-white font-medium rounded-lg hover:bg-brand-dark transition-colors flex items-center justify-center gap-2"
          >
            <Plus size={18} />
            Nuevo Lead
          </button>
        </div>
      </div>

      <LeadsList
        leads={leads || []}
        onEdit={handleEdit}
        onDelete={handleDelete}
        filters={filters}
        setFilters={setFilters}
        canalOptions={canalOptions || []}
        servicioOptions={servicioOptions || []}
      />

      {showForm && (
        <LeadForm
          lead={editingLead}
          leads={leads || []}
          onSave={handleSave}
          onClose={handleClose}
          saving={saving}
          conocioOptions={conocioOptions || []}
          canalOptions={canalOptions || []}
          servicioOptions={servicioOptions || []}
          onSaveOptions={handleSaveOptions}
          savingOptions={savingOptions}
        />
      )}

      {showBuzon && <LeadCaptureDrawer onClose={() => setShowBuzon(false)} />}
    </div>
  );
}
